/**
 * VISTOERA_CANONICAL_PROVIDER_MODEL_CATALOG_NODE_MODEL_BINDING_USAGE_
 * BILLING_FOUNDATION_P0_V1 (task spec §11/§12/§31) — "添加模型" bulk model
 * picker for a Function Node capability. Replaces the old per-node "手工
 * 填写 Model ID" flow: an admin checks any number of already-ENABLED
 * `ProviderModel` rows (grouped by Provider) and binds all of them in one
 * submit (`POST .../node-model-bindings/bulk-add`).
 *
 * Only ENABLED ProviderModel rows are offered (task spec §12 hard rule —
 * never a disabled/deprecated/unavailable model); already-bound models
 * are shown but disabled with a "已添加" tag so re-opening this picker is
 * idempotent-safe.
 */
import { Checkbox, Drawer, Empty, Input, Tag, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { platformBulkAddNodeModelBindings, platformListBindableModels } from '../api'
import type { BindableModelRow } from '../types'
import { platformColors } from './theme'

function errMsg(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { code?: string; detail?: { code?: string; message?: string } } } })
    ?.response?.data
  const code = data?.code || data?.detail?.code
  const detailMessage = data?.detail?.message
  const raw = (err as { message?: string })?.message
  return code ? `${detailMessage || raw || fallback} (${code})` : detailMessage || raw || fallback
}

export default function AddModelDrawer({
  open,
  capability,
  nodeDisplayName,
  onClose,
  onAdded,
}: {
  open: boolean
  capability: string
  nodeDisplayName?: string | null
  onClose: () => void
  onAdded: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<BindableModelRow[]>([])
  const [search, setSearch] = useState('')
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setChecked({})
    setSearch('')
    platformListBindableModels(capability)
      .then((res) => setRows(res.data.items ?? []))
      .catch((err) => message.error(errMsg(err, '加载可绑定模型列表失败')))
      .finally(() => setLoading(false))
  }, [open, capability])

  const grouped = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const filtered = rows.filter((r) => {
      if (!needle) return true
      return (
        r.display_name.toLowerCase().includes(needle) ||
        r.model_external_id.toLowerCase().includes(needle) ||
        (r.family || '').toLowerCase().includes(needle) ||
        (r.provider_display_name || '').toLowerCase().includes(needle)
      )
    })
    const map = new Map<string, { label: string; items: BindableModelRow[] }>()
    for (const row of filtered) {
      const key = row.provider_id
      if (!map.has(key)) map.set(key, { label: row.provider_display_name || row.provider_key || key, items: [] })
      map.get(key)!.items.push(row)
    }
    return Array.from(map.entries())
  }, [rows, search])

  const selectedCount = Object.values(checked).filter(Boolean).length

  const toggle = (id: string, next: boolean) => {
    setChecked((prev) => ({ ...prev, [id]: next }))
  }

  const submit = async () => {
    const ids = Object.entries(checked)
      .filter(([, v]) => v)
      .map(([id]) => id)
    if (ids.length === 0) return
    setSubmitting(true)
    try {
      const res = await platformBulkAddNodeModelBindings({ capability, provider_model_ids: ids })
      message.success(`已添加 ${res.data.items.length} 个模型`)
      onAdded()
      onClose()
    } catch (err) {
      message.error(errMsg(err, '添加模型失败'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer
      title={`添加模型${nodeDisplayName ? ` — ${nodeDisplayName}` : ''}`}
      open={open}
      onClose={onClose}
      width={480}
      destroyOnClose
      extra={
        <button
          type="button"
          disabled={selectedCount === 0 || submitting}
          onClick={() => void submit()}
          style={{
            border: 'none',
            borderRadius: 6,
            padding: '5px 14px',
            background: selectedCount === 0 ? platformColors.border : platformColors.primary,
            color: '#fff',
            cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
            fontSize: 13,
          }}
        >
          {submitting ? '添加中…' : `添加 ${selectedCount || ''} 个模型`.trim()}
        </button>
      }
    >
      <p style={{ fontSize: 12, color: platformColors.mutedText, marginTop: 0, marginBottom: 12 }}>
        只显示已在
        <a href="/platform/models"> 模型管理 </a>
        中启用的模型。勾选后一次性添加，无需逐个手工填写 Model ID。
      </p>
      <Input.Search
        placeholder="搜索模型（例如 Seedance）"
        allowClear
        style={{ marginBottom: 12 }}
        onChange={(e) => setSearch(e.target.value)}
        value={search}
      />
      {!loading && grouped.length === 0 && <Empty description="暂无可绑定的已启用模型" />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {grouped.map(([providerId, group]) => (
          <div key={providerId}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: platformColors.primaryText }}>
              {group.label}
              <span style={{ color: platformColors.mutedText, fontWeight: 400 }}> ({group.items.length})</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {group.items.map((item) => (
                <Checkbox
                  key={item.provider_model_id}
                  disabled={item.already_bound}
                  checked={item.already_bound || Boolean(checked[item.provider_model_id])}
                  onChange={(e) => toggle(item.provider_model_id, e.target.checked)}
                >
                  {item.display_name}
                  <span style={{ color: platformColors.mutedText, fontSize: 11, marginLeft: 6 }}>
                    {item.model_external_id}
                  </span>
                  {item.already_bound && (
                    <Tag color="blue" style={{ marginLeft: 6 }}>
                      已添加
                    </Tag>
                  )}
                </Checkbox>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  )
}
